export default class SidekickModuleSpec {
  id: string; // generated in the code
  name: string;
  presentableName: string;
  presentableDescription: string;
  externalAction: ExternalAction | null;
  btResource?: BTResource | null;
  controlCategories: ControlCategorySpec[] | null;
  modifiers: ModifierSpec[];
  previews: Preview[];
  integrations: string[] | null;
  inMemories: Record<string, string> | null;
  outMemories: Record<string, string> | null;
  allowedCustomerSlug?: string;
}

export class BTResource {
  type: 'thankful-action';
  id: string;
  helpdeskUrl: string;
  metadata: {
    name: string;
    description: string;
  };
}

export class ExternalAction {
  id: string; // generated in the code
  name: string;
  namespace: string;
  integrationId: string;
  displayName: string;
  description?: string;
  dataPull: boolean;
  operation: string;
  inputs: ExternalActionInputOrResult[];
  results: ExternalActionInputOrResult[];
}

export interface ExternalActionInputOrResult {
  name: string;
  displayName: string;
  description?: string;
  typeSpec: TypeSpec;

  // value will not be populated for outputs since outputs will be stored
  // directly in memories.
  value?: unknown;
}

export class TypeSpec {
  id?: string; // TODO: generated in the code
  type: Type;
  null?: boolean;
  props?: Record<string, TypeSpec>;
  items?: TypeSpec;
  enum?: string[];
  name?: string;
  prompt?: string;
}

export enum Type {
  object = 'object',
  string = 'string',
  number = 'number',
  bool = 'bool',
  timestamp = 'timestamp',
  array = 'array',
  FNArray = 'FNArray',
  currency = 'currency',
}

export interface ControlCategorySpec {
  name: string;
  controlGroups: ControlGroupSpec[];
}

export interface ControlGroupSpec {
  name: string;
  controls: ControlSpec[];
}

export enum ControlType {
  ADD_TOPICS = 'AddTopics',
  AI_REPHRASE = 'AIRephrase',
  BOOL = 'bool',
  BUTTON_SET = 'ButtonSet',
  COMMA_SEPARATED_STRING_ARRAY = 'CommaSeparatedStringArray',
  FILTER_BY = 'FilterBy',
  FILTER_FIELD = 'FilterField',
  HIDDEN = 'hidden',
  MEMORY_EXPLORER = 'MemoryExplorer',
  MEMORY_EXPLORER_NAME = 'MemoryExplorerName',
  NUMBER = 'number',
  RULE = 'rule',
  STRING = 'string',
  STRING_ARRAY = 'string[]',
  TEMPLATE = 'template',
  TIMESTAMP = 'timestamp',
  TOPICS_MULTI_SELECT = 'TopicsMultiSelect',
  TEMPLATE_INPUT = 'templateInput',
}

export interface ControlSpec {
  name: string;
  type: ControlType;
  prompt: string;
  defaultValue: ControlValue | undefined;
  enum?: string[];
  isReversed?: boolean;
}

export type ControlValue =
  | string
  | number
  | boolean
  | string[]
  | RuleSet
  | ButtonSet
  | null
  | { rephraseAI: boolean; prompt: string };

export interface Module {
  name: string;
  externalAction: ExternalAction | null;
  btResource: BTResource | null;
  controlValues: Record<string, ControlValue | undefined>;
  modifierValues: ModifierValue[];

  // integrations maps implementations (e.g. OrderListStore) to a
  // CustomerIntegrationID.
  integrations: Record<string, number | null>;

  // generated and stored entirely on the front-end.
  errors?: string[];
}

export interface Condition {
  if: Rule;
  join?: Connective;
}

export interface Rule {
  fields: string[];
  comparator: Comparator;
  rhs: RHS;
}

export enum Comparator {
  equal = '=',
  notEqual = '!=',
  greaterThan = '>',
  lessThan = '<',
  greaterThanOrEqual = '>=',
  lessThanOrEqual = '<=',
  oneOf = 'oneOf',
  notOneOf = 'notOneOf',
  containsOneOf = 'containsOneOf',
  notContainsOneOf = 'notContainsOneOf',
  before = 'before',
  after = 'after',
  on = 'on',
  beforeAbsolute = 'beforeAbsolute',
  afterAbsolute = 'afterAbsolute',
  onAbsolute = 'onAbsolute',
  exists = 'exists',
  doesNotExist = 'doesNotExist',
}

export type RHS = number | string | boolean | string[] | RelativeTime | AbsoluteTime;

export enum Connective {
  and = 'and',
  or = 'or',
}

export interface RuleSet {
  conditions: Condition[];
  then: RuleThen;
}

export interface ButtonSet {
  smart: Button[];
  regular: Button[];
}

export interface Button {
  id?: string;
  label: string;
}

export interface RuleThen {
  pivotFlowID?: number;
  anonymousModule?: Module;
  omitItems?: boolean;
  includeItems?: boolean;
}

export interface RelativeTime {
  num: number;
  period: RelativeTimePeriod;
}

export interface AbsoluteTime {
  date: string;
}

export enum RelativeTimePeriod {
  daysAgo = 'daysAgo',
  bizDaysAgo = 'bizDaysAgo',
  hoursAgo = 'hoursAgo',
  minutesAgo = 'minutesAgo',
}

export interface ModifierValue {
  name: string;
  controlValues: Record<string, ControlValue | undefined>;

  // integrations maps implementations (e.g. OrderListStore) to a
  // CustomerIntegrationID.
  integrations: Record<string, number | null>;

  // generated and stored entirely on the front-end.
  errors?: string[];
}

export interface ModifierSpec {
  name: string;
  presentableName: string;
  presentableDescription: string;
  controlCategories: ControlCategorySpec[] | null;
  previews: Preview[];
  integrations: string[] | null;
  inMemories: Record<string, string> | null;
  outMemories: Record<string, string> | null;
  allowedCustomerSlug?: string;
}

export interface Preview {
  name: string;
  value: string;
  memories: Record<string, string>;
}

export interface VCustomerIntegration {
  customerIntegrationID: number;
  name: string;
  label?: string;
}

const DEFAULT_RULE_SET: RuleSet = {
  conditions: [
    {
      if: {
        fields: [],
        comparator: Comparator.equal,
        rhs: '',
      },
    },
  ],
  then: {
    pivotFlowID: -1,
  },
};

export const CONTROL_TYPE_TO_DEFAULT_VALUE: Record<ControlType, ControlValue> = {
  [ControlType.ADD_TOPICS]: '',
  [ControlType.BOOL]: false,
  [ControlType.BUTTON_SET]: {
    regular: [],
    smart: [],
  },
  [ControlType.FILTER_FIELD]: '',
  [ControlType.NUMBER]: 0,
  [ControlType.RULE]: DEFAULT_RULE_SET,
  [ControlType.STRING]: '',
  [ControlType.STRING_ARRAY]: [],

  // These are not used in the UI, so we don't need to provide a default value.
  [ControlType.MEMORY_EXPLORER]: null,
  [ControlType.MEMORY_EXPLORER_NAME]: null,
  [ControlType.FILTER_BY]: null,
  [ControlType.TOPICS_MULTI_SELECT]: [],
  [ControlType.COMMA_SEPARATED_STRING_ARRAY]: '',
  [ControlType.AI_REPHRASE]: { rephraseAI: false, prompt: '' },
  [ControlType.TIMESTAMP]: null,
  [ControlType.HIDDEN]: null,
  [ControlType.TEMPLATE]: null,
  [ControlType.TEMPLATE_INPUT]: null,
};
